import React, { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import TauriSectionModalVisual from '../TauriSectionBaseVisual/TauriSectionModalVisual/TauriSectionModalVisual';
import TauriSectionModalMultiVisual from '../TauriSectionBaseVisual/TauriSectionModalMultiVisual/TauriSectionModalMultiVisual';
import setting from '../../config.json';

const TauriComboVisual = ({ chartConfig, chartData, chartOptions, columnSize, rowSize, palettes, themes, chartPalettes }) => {
    const chartRef = useRef(null);
    const [selectedVisual, setSelectedVisual] = useState(null);
    const [selectedData, setSelectedData] = useState(null);
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (!chartData) return;

        const handleDataPointSelection = (event, chartContext, config) => {
            const { dataPointIndex } = config;

            const title = chartConfig.binding.context.point.onClick.selectedValueColumn;
            const modalTitle = chartConfig.binding.context.point.onClick.selectedTitle;
            const selectedDesc = chartData[dataPointIndex][chartConfig.binding.data.title];
            const selectedVal = chartData[dataPointIndex][title];
            const selectedTitle = chartConfig.title;

            if (chartConfig.binding.context.point.onClick.visual || Array.isArray(chartConfig.binding.context.point.onClick.visuals)) {
                let selectedVisual = null;
                if (chartConfig.binding.context.point.onClick.visuals) {
                    selectedVisual = chartConfig.binding.context.point.onClick.visuals;
                } else {
                    // Handle single visual
                    selectedVisual = chartConfig.binding.context.point.onClick.visual;
                }

                setSelectedVisual(selectedVisual);
                setSelectedData({ title, selectedDesc, selectedVal, selectedTitle, modalTitle });

                const tauriElement = document.querySelector('.tauri-host-container');
                if (tauriElement) {
                    if (event) {
                        const { left, top, right, bottom } = tauriElement.getBoundingClientRect();
                        let posX = event.clientX;
                        let posY = event.clientY;
                        if ((event.clientX + setting.modalMinWidth) > window.innerWidth) {
                            posX = event.clientX - setting.modalMinWidth;
                        }
                        if ((event.clientY + setting.modalMinHeight) > window.innerHeight) {
                            posY = event.clientY - setting.modalMinHeight;
                        }

                        setModalPosition({ x: posX, y: posY });
                    }
                }
            }
        };

        const series = chartConfig.binding.data.series.map((seriesItem, index) => ({
            name: seriesItem.name,
            data: chartData.map(item => item[seriesItem.value] ?? null),
            color: chartPalettes[index]?.ranges?.[0] ?? null,
            type: seriesItem.type,
            yAxisIndex: seriesItem.type === 'line' ? 1 : 0,
            percent: seriesItem.percent
        }));

        const primarySeries = series.filter(seriesItem => seriesItem.yAxisIndex === 0);
        const secondarySeries = series.filter(seriesItem => seriesItem.yAxisIndex === 1);

        const primaryMaxValue = Math.max(...primarySeries.flatMap(seriesItem => seriesItem.data.filter(d => d != null)));
        const secondaryMaxValue = Math.max(...secondarySeries.flatMap(seriesItem => seriesItem.data.filter(d => d != null)));

        const updatedYAxis = series.map((seriesItem, index) => {
            const isSecondary = seriesItem.type === 'line';
            const isHighestPrimary = !isSecondary && Math.max(...seriesItem.data) === primaryMaxValue;
            const isHighestSecondary = isSecondary && Math.max(...seriesItem.data) === secondaryMaxValue;

            return {
                min: 0,
                opposite: isSecondary,
                axisTicks: { show: true },
                axisBorder: { show: true, color: seriesItem.color },
                // title: {
                //     text: seriesItem.name,
                //     style: { fontWeight: 'normal', fontFamily: 'Lato-Regular', color: seriesItem.color },
                // },
                labels: {
                    formatter: isSecondary
                        ? (value) => {
                            if (seriesItem.percent) {
                                return `${value.toLocaleString(undefined, { maximumFractionDigits: setting.DISPLAYED_DECIMALS })}%`;
                            } else {
                                return value.toLocaleString(undefined, { maximumFractionDigits: setting.DISPLAYED_DECIMALS });
                            }
                        }
                        : (value) => formatNumber(value),
                    style: { fontFamily: 'Lato-Regular' },
                },
                max: isSecondary ? secondaryMaxValue : primaryMaxValue,
                show: isSecondary ? isHighestSecondary : isHighestPrimary, // Only show the y-axis with the highest max value
            };
        });

        const formatNumber = (value) => {
            if (Math.abs(value) >= 1.0e+9) {
                return (value / 1.0e+9).toFixed(1) + 'B'; // Billions
            }
            if (Math.abs(value) >= 1.0e+6) {
                return (value / 1.0e+6).toFixed(1) + 'M'; // Millions
            }
            if (Math.abs(value) >= 1.0e+3) {
                return (value / 1.0e+3).toFixed(1) + 'K'; // Thousands
            }
            return value.toLocaleString(); // Default formatting
        };

        const options = {
            chart: {
                ...chartOptions.chart,
                toolbar: {
                    show: true,
                    tools: {
                        zoomin: true,
                        zoomout: true,
                        reset: true,
                        pan: true,
                        zoom: true,
                        selection: false,
                        download: true,
                    }
                },
                events: { dataPointSelection: handleDataPointSelection },
            },
            tooltip: {
                y: {
                    formatter: function (value, { seriesIndex }) {
                        const seriesItem = series[seriesIndex];

                        if (value !== undefined && value !== null) {
                            if (seriesItem.percent) {
                                return `${value.toLocaleString(undefined, { maximumFractionDigits: setting.DISPLAYED_DECIMALS })}%`;
                            } else {
                                return value.toLocaleString(undefined, { maximumFractionDigits: setting.DISPLAYED_DECIMALS });
                            }
                        } else {
                            return '';
                        }
                    }
                },
                x: {
                    formatter: function (_, { dataPointIndex }) {
                        return chartData[dataPointIndex][chartConfig.binding.data.title] || '';
                    }
                },
                style: chartOptions.tooltip.style
            },
            xaxis: {
                ...chartOptions.xaxis,
                categories: chartData.map(item => item[chartConfig.binding.data.title] || ''),
                title: { ...chartOptions.xaxis.title, text: chartConfig.xAxis },
                labels: {
                    style: { fontFamily: 'Lato-Regular' },
                    formatter: (value) => value?.length > 10 ? `${value.slice(0, 10)}...` : value || '',
                },
            },
            yaxis: updatedYAxis,
            series,
            grid: chartOptions.grid,
            legend: chartOptions.legend,
            dataLabels: chartOptions.dataLabels,
            plotOptions: {
                bar: { ...chartOptions.plotOptions.bar, horizontal: false },
            },
            stroke: chartOptions.stroke,
        };

        const chart = new ApexCharts(chartRef.current, options);
        chart.render();

        return () => chart.destroy();
    }, [chartConfig, chartData]);

    return (
        <div>
            <div className='chart-content'>
                <div ref={chartRef} />
            </div>
            {selectedVisual && (
                chartConfig.binding.context.point.onClick.visuals ? (
                    <TauriSectionModalMultiVisual
                        show={selectedVisual !== null}
                        onHide={() => setSelectedVisual(null)}
                        selectedData={selectedData}
                        visual={selectedVisual}
                        columnSize={columnSize}
                        rowSize={rowSize}
                        style={{ top: modalPosition.y, left: modalPosition.x }}
                        palettes={palettes}
                        themes={themes}
                    />
                ) : (
                    <TauriSectionModalVisual
                        show={selectedVisual !== null}
                        onHide={() => setSelectedVisual(null)}
                        selectedData={selectedData}
                        visual={selectedVisual}
                        columnSize={columnSize}
                        rowSize={rowSize}
                        style={{ top: modalPosition.y, left: modalPosition.x }}
                        palettes={palettes}
                        themes={themes}
                    />
                )
            )}
        </div>
    );
};

export default TauriComboVisual;
